import { get, post, put, del } from './client';
import type {
  Service,
  ServiceListResponse,
  CreateServiceRequest,
  UpdateServiceRequest,
  ListServicesParams,
} from '@/types/service';

/**
 * List all services with optional filtering and pagination.
 * Platform users see all services, merchant users see only their own.
 *
 * @param params Optional query parameters for filtering/pagination
 * @returns Paginated list of services
 */
export async function listServices(params?: ListServicesParams): Promise<ServiceListResponse> {
  const queryParams: Record<string, string> = {};

  if (params?.cursor) queryParams.cursor = params.cursor;
  if (params?.limit) queryParams.limit = String(params.limit);
  if (params?.sortBy) queryParams.sort_by = params.sortBy;
  if (params?.sortDir) queryParams.sort_dir = params.sortDir;
  if (params?.status) queryParams.status = params.status;
  if (params?.merchantId) queryParams.merchantId = params.merchantId;
  if (params?.gatewayServiceId) queryParams.gatewayServiceId = params.gatewayServiceId;

  return get<ServiceListResponse>('/admin/services', queryParams);
}

/**
 * Get a single service by ID.
 * Spec 0027: Optional merchantId hint for platform_admin routing via Facade.
 *
 * @param serviceId The service ID (pattern: svc_...)
 * @param merchantId Optional merchantId hint (passed as query param for platform_admin)
 * @returns Service details
 * @throws ApiError with 404 if service not found
 */
export async function getService(serviceId: string, merchantId?: string): Promise<Service> {
  const params: Record<string, string> = {};
  if (merchantId) params.merchantId = merchantId;
  return get<Service>(`/admin/services/${encodeURIComponent(serviceId)}`, params);
}

/**
 * Create a new service.
 * This will validate the request, call Facade API to create the service,
 * and store in local registry with facadeServiceId reference.
 *
 * @param data Service creation data
 * @returns Created service
 * @throws ApiError with 400 for validation errors
 * @throws ApiError with 422 for theme asset validation failures
 */
export async function createService(data: CreateServiceRequest): Promise<Service> {
  return post<Service>('/admin/services', data);
}

/**
 * Update an existing service.
 * This will validate the request, call Facade API to update the service,
 * and update local registry.
 *
 * @param serviceId The service ID to update
 * @param data Service update data (all fields optional)
 * @returns Updated service
 * @throws ApiError with 404 if service not found
 * @throws ApiError with 422 for theme asset validation failures
 */
export async function updateService(
  serviceId: string,
  data: UpdateServiceRequest
): Promise<Service> {
  return put<Service>(`/admin/services/${encodeURIComponent(serviceId)}`, data);
}

/**
 * Deactivate (soft delete) a service.
 * Sets service status to 'inactive'.
 *
 * @param serviceId The service ID to deactivate
 * @returns Deactivated service
 * @throws ApiError with 404 if service not found
 */
export async function deleteService(serviceId: string): Promise<Service> {
  return del<Service>(`/admin/services/${encodeURIComponent(serviceId)}`);
}

/**
 * Approve legal links for a service (Spec 0040 M-7).
 * Sets legalLinksStatus to 'approved'.
 */
export async function approveLegalLinks(serviceId: string): Promise<Service> {
  return updateService(serviceId, { legalLinksStatus: 'approved' });
}

/**
 * Reject legal links for a service (Spec 0040 M-7).
 * Sets legalLinksStatus to 'rejected'.
 */
export async function rejectLegalLinks(serviceId: string): Promise<Service> {
  return updateService(serviceId, { legalLinksStatus: 'rejected' });
}
